import React, { useState, useEffect, useRef } from 'react';
import { User, Role } from '../types';
import { db } from '../services/mockDb';
import { env } from '../services/env';
import { isBackendAuthEnabled, loginTeacherWithBackend } from '../services/authService';

interface LoginProps {
  onLogin: (user: User) => void;
}

// Helper to decode JWT from Google
const decodeJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
  return JSON.parse(jsonPayload);
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  
  // Form State
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('teacher');
  
  const [error, setError] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [missingConfig, setMissingConfig] = useState(false);
  const [schoolId, setSchoolId] = useState('VJHS-001');
  const [teacherId, setTeacherId] = useState('T-PRITI-001');
  const [teacherPin, setTeacherPin] = useState('');
  
  const backendAuthEnabled = isBackendAuthEnabled();
  const intervalRef = useRef<number | null>(null);

  // Access env var injected by Vite
  const SCHOOL_DOMAIN = env.schoolDomain;

  // Initialize Google Button
  useEffect(() => {
    const CLIENT_ID = env.googleClientId;
    setGoogleEnabled(false);
    setConfigError(null);

    // 2. Graceful Fallback if missing
    if (!CLIENT_ID) {
      setMissingConfig(true);
      setGoogleEnabled(false);
      return;
    }
    setMissingConfig(false);

    // Function to try initializing Google Sign-In
    const initializeGSI = () => {
      if (window.google && window.google.accounts) {
        if (CLIENT_ID.startsWith("GOCSPX")) {
          setConfigError("Invalid ID: You are using a Client Secret (starts with GOCSPX). Use Client ID.");
          return true; // Stop retrying
        }

        try {
          const config: any = {
            client_id: CLIENT_ID,
            callback: handleGoogleResponse
          };

          if (SCHOOL_DOMAIN) {
            config.hosted_domain = SCHOOL_DOMAIN;
          }

          window.google.accounts.id.initialize(config);
          
          const btnParent = document.getElementById("googleBtn");
          if (btnParent) {
            window.google.accounts.id.renderButton(
              btnParent,
              { theme: "outline", size: "large", width: "100%", text: isLogin ? "signin_with" : "signup_with" }
            );
            setGoogleEnabled(true);
          }
          return true; // Success
        } catch (e) {
          console.error("Google Sign-In Initialization Error:", e);
          setConfigError("Failed to load Google Sign-In script.");
          return true; // Stop retrying on error
        }
      }
      return false; // Not ready yet
    };

    // Try immediately
    if (!initializeGSI()) {
      // Retry every 100ms for up to 5 seconds
      let attempts = 0;
      intervalRef.current = window.setInterval(() => {
        attempts++;
        if (initializeGSI() || attempts > 50) {
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      }, 100);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isLogin, role, SCHOOL_DOMAIN]);

  const handleGoogleResponse = (response: any) => {
    try {
      const payload = decodeJwt(response.credential);
      
      if (SCHOOL_DOMAIN) {
        if (payload.hd !== SCHOOL_DOMAIN && !payload.email.endsWith(`@${SCHOOL_DOMAIN}`)) {
          setError(`Access restricted. You must use a ${SCHOOL_DOMAIN} email address.`);
          return;
        }
      }

      const user = db.loginOrRegisterGoogleUser(
        payload.email,
        payload.name,
        payload.picture,
        role 
      );

      onLogin(user);
    } catch (err) {
      console.error(err);
      setError("Failed to sign in with Google.");
    }
  };

  const resetForm = () => {
    setName('');
    setUsername('');
    setPassword('');
    setError('');
    setSuccessMsg('');
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setIsLoading(true);

    try {
      if (isLogin && backendAuthEnabled && role === 'teacher') {
        const user = await loginTeacherWithBackend(schoolId, teacherId, teacherPin);
        onLogin(user);
        return;
      }

      if (isLogin) {
        const user = db.authenticate(username, password);
        if (user) {
          onLogin(user);
        } else {
          setError('Invalid username or password');
        }
      } else {
        if (!name || !username || !password) {
          throw new Error('All fields are required');
        }
        const newUser = db.registerUser(name, username, password, role);
        setSuccessMsg('Account created successfully! Logging you in...');
        window.setTimeout(() => onLogin(newUser), 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-card border border-stone-200 p-8">
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-full bg-accent flex items-center justify-center text-white font-serif text-3xl font-bold mx-auto mb-6">
            व
          </div>
          <h1 className="font-serif text-3xl font-bold text-stone-900 mb-2">
            {isLogin ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-stone-500">
            {isLogin ? 'Sign in to Vishwajyot AI Teacher Co-pilot' : 'Join as a Teacher or Student'}
          </p>
        </div>

        <div className="mb-6 animate-fade-in">
           <div className="grid grid-cols-2 gap-3">
             <button
               type="button"
               onClick={() => setRole('teacher')}
               className={`py-2 px-4 rounded-lg border text-sm font-medium transition-all ${
                 role === 'teacher' 
                   ? 'bg-primary-50 border-primary-500 text-primary-700' 
                   : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
               }`}
             >
               👩‍🏫 Teacher
             </button>
             <button
               type="button"
               onClick={() => setRole('student')}
               className={`py-2 px-4 rounded-lg border text-sm font-medium transition-all ${
                 role === 'student' 
                   ? 'bg-primary-50 border-primary-500 text-primary-700' 
                   : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
               }`}
             >
               🧑‍🎓 Student
             </button>
           </div>
        </div>

        <div className="space-y-4">
           {/* Google Button Container - Only shows if config exists */}
           <div id="googleBtn" className={`min-h-[40px] ${!googleEnabled ? 'hidden' : ''}`}></div>
           
           {/* Visual Placeholder for Missing Config */}
           {missingConfig && (
              <div className="relative group w-full">
                <button 
                  disabled 
                  className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-400 px-4 py-2.5 rounded shadow-sm cursor-not-allowed hover:bg-gray-50 transition-colors opacity-80"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                  </svg>
                  <span className="text-sm font-medium">Sign in with Google</span>
                </button>
                {/* Tooltip */}
                <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md shadow-lg w-64 text-center z-10">
                  Feature disabled: <code>GOOGLE_CLIENT_ID</code> is missing in .env file.
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
           )}

           {configError && (
              <div className="p-3 bg-red-50 text-red-600 text-xs rounded-lg text-center border border-red-200">
                <strong>Configuration Error:</strong><br/>
                {configError}
              </div>
           )}
           
           {SCHOOL_DOMAIN && (
             <p className="text-xs text-center text-gray-400">
               School login restricted to <strong>@{SCHOOL_DOMAIN}</strong>
             </p>
           )}

           <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {googleEnabled ? 'Or continue with username' : 'Sign in with username'}
                </span>
              </div>
            </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {isLogin && backendAuthEnabled && role === 'teacher' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">School ID</label>
                <input
                  type="text"
                  required
                  value={schoolId}
                  onChange={(e) => setSchoolId(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  placeholder="VJHS-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teacher ID</label>
                <input
                  type="text"
                  required
                  value={teacherId}
                  onChange={(e) => setTeacherId(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  placeholder="T-PRITI-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PIN</label>
                <input
                  type="password"
                  required
                  value={teacherPin}
                  onChange={(e) => setTeacherPin(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  placeholder="••••••••"
                />
              </div>
              <p className="text-xs text-gray-500">
                Grading runs securely on Cloudflare. Your Gemini key never ships to the browser.
              </p>
            </>
          ) : (
            <>
          {!isLogin && (
            <div className="animate-fade-in">
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                required={!isLogin}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                placeholder="e.g. Mrs. Sarah Jones"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
            <input
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
              placeholder="Choose a username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
              placeholder="••••••••"
            />
          </div>
            </>
          )}

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2 animate-fade-in">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
            </div>
          )}

          {successMsg && (
            <div className="p-3 bg-green-50 text-green-600 text-sm rounded-lg flex items-center gap-2 animate-fade-in">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              {successMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-accent text-white font-semibold py-3 px-4 rounded-xl shadow-sm hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-all ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
          >
            {isLoading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button 
            onClick={toggleMode}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-3">Or try Demo Credentials</p>
          <div className="grid grid-cols-2 gap-4 text-left mb-4">
            <div className="bg-gray-50 p-3 rounded border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => {setIsLogin(true); setRole('teacher'); if (backendAuthEnabled) { setSchoolId('VJHS-001'); setTeacherId('T-PRITI-001'); setTeacherPin('Priti@123'); } else { setUsername('Priti Dhandrut'); setPassword('Priti@123'); }}}>
              <div className="text-xs font-bold text-gray-700">Teacher</div>
              <div className="text-xs text-gray-500 font-mono mt-1">{backendAuthEnabled ? 'VJHS-001 / T-PRITI-001' : 'Priti Dhandrut'}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => {setIsLogin(true); setUsername('alex'); setPassword('password');}}>
              <div className="text-xs font-bold text-gray-700">Student</div>
              <div className="text-xs text-gray-500 font-mono mt-1">alex</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
